// @spec SHL-STR-020
// @spec SHL-STR-021

import { useNavigate } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';
import { toast } from 'sonner';

import { getCommandById } from '@/components/command-palette/command-registry';
import { useRecentCommands } from '@/hooks/use-recent-commands';
import i18n from '@/lib/i18n';

export interface UseCommandPaletteReturn {
  /** Execute a command by its ID */
  executeCommand: (commandId: string) => Promise<void>;
  /** Check if the input indicates command mode */
  isCommandMode: (input: string) => boolean;
  /** Strip command prefix from input */
  getCommandFilter: (input: string) => string;
}

/**
 * Hook for command palette operations.
 *
 * Provides utilities for:
 * - Executing commands
 * - Detecting command mode from input
 * - Managing command filter
 * - Toast notifications for error states
 *
 * @example
 * const { executeCommand, isCommandMode, getCommandFilter } = useCommandPalette();
 *
 * // Check if user is in command mode
 * if (isCommandMode(search)) {
 *   // Show command palette
 *   const filter = getCommandFilter(search);
 * }
 *
 * // Execute a selected command
 * await executeCommand('go-dashboard');
 */
export function useCommandPalette(): UseCommandPaletteReturn {
  const navigate = useNavigate();
  const { addRecentCommand } = useRecentCommands();

  const executeCommand = useCallback(
    async (commandId: string) => {
      const command = getCommandById(commandId);

      if (!command) {
        toast.error(i18n.t('common:toast.commandNotFound'), {
          description: 'The requested command is not available.',
        });
        return;
      }

      if (command.disabled) {
        toast.error(i18n.t('common:toast.commandUnavailable'), {
          description: 'This command is currently disabled.',
        });
        return;
      }

      try {
        const result = await command.action();

        // Track as recent command
        addRecentCommand(commandId);

        // If the action returns a path, navigate to it
        if (typeof result === 'string') {
          await navigate({ to: result });
        }
      } catch (error) {
        console.error(`Command ${commandId} failed:`, error);
        toast.error(i18n.t('common:toast.commandFailed'), {
          description: 'An error occurred while executing the command.',
        });
      }
    },
    [navigate, addRecentCommand]
  );

  const isCommandMode = useCallback((input: string): boolean => {
    return input.startsWith('/') || input.startsWith('>');
  }, []);

  const getCommandFilter = useCallback((input: string): string => {
    if (input.startsWith('/') || input.startsWith('>')) {
      return input.slice(1).trim();
    }
    return input;
  }, []);

  return useMemo(
    () => ({
      executeCommand,
      isCommandMode,
      getCommandFilter,
    }),
    [executeCommand, isCommandMode, getCommandFilter]
  );
}
